import React, { useState, useMemo, useEffect } from 'react';
import * as Yup from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { ContactForm, ContactReason, ContractOptionType } from 'core/models';
import { OptionType, SGSButton, SGSForm, SGSFormInput, SGSFormRadio, SGSFormSwitch, SGSRadio } from 'components';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, FormHelperText } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'core/hooks';
import { contactService } from 'core/services';
import SGSFormAutoComplete from 'components/Form/AutoComplete';
import { Link } from 'react-router-dom';
import idee from '../../../assets/idee.svg';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { useAppContext } from 'core/contexts';
import styles from './Contact.module.scss';
import classNames from 'classnames';
import { isVisitor } from 'core/utils/roles';

type Props = {};

enum TypeContact {
    PHONE = 'phone',
    EMAIL = 'email',
    POSTAL = 'postal',
}
const typesContact: OptionType<string>[] = [
    {
        label: 'contact.type.phone',
        value: TypeContact.PHONE,
    },
    {
        label: 'contact.type.email',
        value: TypeContact.EMAIL,
    },
    {
        label: 'contact.type.postal',
        value: TypeContact.POSTAL,
    },
];

const ContactPage: React.FC<Props> = () => {
    const { t } = useTranslation();
    const { user } = useAppContext();
    const [typeContact, setTypeContact] = useState<TypeContact>(TypeContact.PHONE);

    const { data, loading } = useQuery(contactService.getContratsExtended);
    const [isSendingForm, setIsSendingForm] = useState(false);
    const [resetContrat, setResetContrat] = useState('');
    const [copieByMail, setCopieByMail] = useState(false);
    const [contractResponsable, setContractResponsable] = useState(null);

    const contactValidation: Yup.AnyObjectSchema = useMemo(() => {
        switch (typeContact) {
            case TypeContact.PHONE:
                return Yup.object()
                    .shape({
                        personnel: Yup.string(),
                        mobile: Yup.string(),
                        bureau: Yup.string(),
                    })
                    .test('at-least-one-phone', t('contact.atLeastOnePhoneRequired'), ({ personnel, bureau, mobile }) => (!personnel && !bureau && !mobile ? false : true));
            case TypeContact.EMAIL:
                return Yup.object({
                    email: Yup.string().email(t('contact.validation.emailInvalid')).required(),
                });
            case TypeContact.POSTAL:
                return Yup.object({
                    adresse: Yup.string().required(),
                });
            default:
                return Yup.object();
        }
    }, [typeContact, t]);

    const makeConditionalSchema = (typeContact) => {
        if (typeContact === 'email') {
            return Yup.string();
        }
        return Yup.string().when(['recevoirUneCopie'], {
            is: (recevoirUneCopie) => recevoirUneCopie === true,
            then: Yup.string().email(t('contact.validation.emailInvalid')).required(),
        });
    };

    const validationSchema: Yup.SchemaOf<ContactForm> = Yup.object().shape({
        idContrat: Yup.string().required(),
        commentaire: Yup.string().required(),
        raison: Yup.mixed().oneOf(Object.values(ContactReason)).required(),
        recevoirUneCopie: Yup.bool().required(),
        contact: contactValidation,
        user: Yup.string(),
        firstname: Yup.string().required(),
        lastname: Yup.string().required(),
        company: Yup.string().required(),
        emailCopy: makeConditionalSchema(typeContact),
    });

    const methods = useForm<ContactForm>({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            recevoirUneCopie: false,
            raison: ContactReason.INFO_GARANTIES_DECOMPTES,
        },
    });
    const {
        handleSubmit,
        reset,
        watch,
        getValues,
        formState: { errors },
    } = methods;

    // eslint-disable-next-line
    const setContact = (firstname, lastname, company) => {
        methods.setValue('firstname', firstname);
        methods.setValue('lastname', lastname);
        methods.setValue('company', company);
    };

    useEffect(() => {
        setContact(user.prenom, user.nom, '');
    }, []);

    useEffect(() => {
        setContact(methods.getValues('firstname'), methods.getValues('lastname'), methods.getValues('company'));
    }, [setContact, user.prenom, user.nom]);

    const watchEmail = methods.watch('recevoirUneCopie', false);
    useEffect(
        () => {
            const hasCopieByEmail = methods.getValues('recevoirUneCopie');
            setCopieByMail(hasCopieByEmail && typeContact !== TypeContact.EMAIL);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [watchEmail, typeContact],
    );

    function sendContactForm(values: ContactForm) {
        if (typeContact !== 'email') {
            const contact: any = { ...values.contact };
            contact.email = values.emailCopy;
            values.contact = contact;
        }

        values.user = { id: user.id, prenom: user.prenom, nom: user.nom, email: user.login, num_adherent: user.num_adherent };
        setIsSendingForm(true);
        contactService
            .sendContactForm(values)
            .then(() => {
                const prenom = methods.getValues('firstname');
                const nom = methods.getValues('lastname');
                const company = methods.getValues('company');
                resetForm();
                setContact(prenom, nom, company);
                toast.success('Demande envoyée avec succès');
            })
            .finally(() => {
                setIsSendingForm(false);
            });
    }

    const resetForm = () => {
        reset();
        setResetContrat(uuidv4());
        setTypeContact(TypeContact.PHONE);
        setContact(user.prenom, user.nom, '');
    };

    const watchContract = watch('idContrat');
    const watchRaison = watch('raison');

    useEffect(() => {
        setContractResponsable(null);

        const contract: ContractOptionType | undefined = data?.find((c: ContractOptionType) => c.value === watchContract);

        if (!contract) {
            return;
        }

        let responsable = null;
        const raison: ContactReason = getValues('raison');

        switch (raison) {
            case ContactReason.INFO_ADHESION:
                responsable = contract.lbRespadhes;
                break;
            case ContactReason.INFO_GARANTIES_DECOMPTES:
                responsable = contract.lbResppresta;
                break;
            case ContactReason.INFO_COMMERCIAL:
                responsable = process.env.REACT_APP_CONTACT_INFO_COMMERCIAL;
                break;
            case ContactReason.INFO_COTISATION:
                responsable = process.env.REACT_APP_CONTACT_POLE_COTISATION;
                break;
            case ContactReason.RECLAMATION:
                responsable = process.env.REACT_APP_CONTACT_RECLAMATION;
                break;
            case ContactReason.SUPPORT_TECHNIQUE:
                responsable = process.env.REACT_APP_CONTACT_SUPPORT_TECHNIQUE;
                break;
            default:
                responsable = null;
                break;
        }

        setContractResponsable(watchContract ? responsable : null);
    }, [watchContract, watchRaison, data, getValues]);

    return (
        <FormProvider {...methods}>
            <SGSForm.Card onSubmit={handleSubmit(sendContactForm)}>
                <SGSForm.Row>
                    <SGSForm.Col>
                        <SGSFormAutoComplete variant="outlined" name="idContrat" label="contact.contract" options={data ?? []} loading={loading} key={resetContrat} defaultValue="" />
                    </SGSForm.Col>
                </SGSForm.Row>

                {contractResponsable && contractResponsable !== process.env.REACT_APP_CONTACT_SUPPORT_TECHNIQUE && (
                    <Box display={'flex'} flexDirection={'row'} className={'mb-4'}>
                        <Box flex={'1 1 0px'} className={classNames(styles.title, 'm-0 text-right')}>
                            Votre gestionnaire pour ce contrat :
                        </Box>
                        <Box flex={'1 1 0px'} className=" pl-3 font-weight-bold" dangerouslySetInnerHTML={{ __html: contractResponsable }} />
                    </Box>
                )}

                <SGSForm.Row>
                    <SGSForm.Col md={6}>
                        <SGSFormRadio
                            name="raison"
                            label="contact.why"
                            options={Object.keys(ContactReason).map((o) => ({
                                label: ContactReason[o],
                                value: ContactReason[o],
                            }))}
                        />
                        <SGSFormInput variant="outlined" name="commentaire" label="contact.comments" multiline />
                        <Box className="mt-3 text-center" style={{ textDecoration: 'underline' }}>
                            <img src={idee} width={'30'} height={'30'} className={'mr-1'} alt="" />{' '}
                            <Link to={'/boite_a_idees'} style={{ color: '#3399db ' }}>
                                Aidez-nous à améliorer notre site !
                            </Link>
                        </Box>
                    </SGSForm.Col>
                    <SGSForm.Col md={6}>
                        {/* ajouter nom + prenom + société */}
                        <Box display="flex" flexDirection="column">
                            <Box className={styles.title}>Contact</Box>
                            <SGSForm.Stack>
                                <SGSFormInput variant="outlined" label="contact.firstname" name="firstname" />
                                <SGSFormInput variant="outlined" label="contact.lastname" name="lastname" />
                                <SGSFormInput variant="outlined" label="contact.company" name="company" />
                            </SGSForm.Stack>
                        </Box>

                        <Box display="flex" flexDirection="column" className="mt-2">
                            <SGSRadio label="contact.contactType" row value={typeContact} onChange={(e) => setTypeContact(e.target.value as TypeContact)} options={typesContact}></SGSRadio>

                            {typeContact === TypeContact.PHONE && (
                                <>
                                    <SGSForm.Stack>
                                        <SGSFormInput variant="outlined" label="contact.phone.perso" name="contact.personnel" />
                                        <SGSFormInput variant="outlined" label="contact.phone.mobile" name="contact.mobile" />
                                        <SGSFormInput variant="outlined" label="contact.phone.office" name="contact.bureau" />
                                    </SGSForm.Stack>
                                    {errors.contact && <FormHelperText error>{(errors.contact as any).message}</FormHelperText>}
                                </>
                            )}

                            {typeContact === TypeContact.EMAIL && <SGSFormInput variant="outlined" label="contact.email" name="contact.email" />}

                            {typeContact === TypeContact.POSTAL && <SGSFormInput variant="outlined" label="contact.address" name="contact.adresse" multiline />}
                        </Box>

                        <SGSFormSwitch name="recevoirUneCopie" label="contact.receiveCopy" />

                        {copieByMail && (
                            <Box display="flex" flexDirection="column">
                                <SGSForm.Stack>
                                    <SGSFormInput variant="outlined" label="email" name="emailCopy" />
                                </SGSForm.Stack>
                            </Box>
                        )}
                    </SGSForm.Col>
                </SGSForm.Row>

                <SGSForm.Actions>
                    <SGSButton type="button" className="mr-2" onClick={() => resetForm()}>
                        Annuler
                    </SGSButton>
                    <SGSButton type="submit" loading={isSendingForm} disabled={isVisitor(user)}>
                        {t('contact.send')}
                    </SGSButton>
                </SGSForm.Actions>
            </SGSForm.Card>
        </FormProvider>
    );
};

export default ContactPage;
